
import { X, Calendar, Target, TrendingUp, Clock, Flame, CheckCircle2, SkipForward, ChevronLeft, ChevronRight } from 'lucide-react';
import { PACT } from '@/types/pact';
import { format, isToday, parseISO, eachDayOfInterval, startOfDay, startOfMonth, endOfMonth, eachWeekOfInterval, getWeeksInMonth, startOfWeek, endOfWeek, isSameMonth, addMonths, subMonths } from 'date-fns';
import { useState, useEffect } from 'react';

interface PACTDetailModalProps {
  pact: PACT | null;
  isOpen: boolean;
  onClose: () => void;
  getStreak: (pact: PACT) => number;
  toggleCompletion: (pactId: string, date: string) => void;
  toggleSkip: (pactId: string, date: string) => void;
  // Add this to get fresh data
  allPacts: PACT[];
}

const PACTDetailModal = ({ pact: initialPact, isOpen, onClose, getStreak, toggleCompletion, toggleSkip, allPacts }: PACTDetailModalProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Get the fresh pact data from allPacts
  const pact = initialPact ? allPacts.find(p => p.id === initialPact.id) || initialPact : null;

  if (!isOpen || !pact) return null;

  console.log('PACTDetailModal rendered with pact:', pact);

  const createdDate = new Date(pact.createdAt);
  const currentDate = new Date();
  const daysSinceCreated = Math.floor((currentDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
  const daysRemaining = Math.max(0, pact.duration - daysSinceCreated);
  const completionRate = Math.round(((pact.completedDays?.length || 0) / Math.max(1, daysSinceCreated + 1)) * 100);
  
  // Get all days in the PACT period
  const endDate = new Date(createdDate);
  endDate.setDate(endDate.getDate() + pact.duration - 1);
  
  const allDays = eachDayOfInterval({
    start: createdDate,
    end: new Date(Math.min(currentDate.getTime(), endDate.getTime()))
  });

  const completedDays = (pact.completedDays || []).map(date => startOfDay(parseISO(date)));
  const skippedDays = (pact.skippedDays || []).map(date => startOfDay(parseISO(date)));
  const missedDays = allDays.filter(day => {
    const dayStr = format(day, 'yyyy-MM-dd');
    const isCompleted = (pact.completedDays || []).includes(dayStr);
    const isSkipped = (pact.skippedDays || []).includes(dayStr);
    const isPast = day < startOfDay(currentDate);
    return isPast && !isCompleted && !isSkipped && !isToday(day);
  });

  const currentStreak = getStreak(pact);
  const totalCompleted = pact.completedDays?.length || 0;
  const totalMissed = missedDays.length;
  const totalSkipped = pact.skippedDays?.length || 0;
  const skipsRemaining = (pact.maxSkips || 0) - totalSkipped;
  const totalDaysElapsed = allDays.length;

  // Calendar functionality
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  
  const calendarDays = eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd
  });

  const getDayStatus = (day: Date) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    const startOfPACT = startOfDay(createdDate);
    const endOfPACT = startOfDay(endDate);
    const dayStart = startOfDay(day);
    
    console.log(`getDayStatus for ${dayStr}: startOfPACT=${format(startOfPACT, 'yyyy-MM-dd')}, endOfPACT=${format(endOfPACT, 'yyyy-MM-dd')}, dayStart=${format(dayStart, 'yyyy-MM-dd')}`);
    
    const isPACTPeriod = dayStart >= startOfPACT && dayStart <= endOfPACT;
    const isCompleted = (pact.completedDays || []).includes(dayStr);
    const isSkipped = (pact.skippedDays || []).includes(dayStr);
    const isPast = dayStart < startOfDay(currentDate);
    const isCurrentDay = isToday(dayStart);
    
    console.log(`Day ${dayStr}: isPACTPeriod=${isPACTPeriod}, isCompleted=${isCompleted}, isSkipped=${isSkipped}, isPast=${isPast}, isCurrentDay=${isCurrentDay}`);
    
    if (!isPACTPeriod) return 'outside';
    if (isCompleted) return 'completed';
    if (isSkipped) return 'skipped';
    if (isCurrentDay) return 'today';
    if (isPast) return 'missed';
    return 'upcoming';
  };

  const handleDayClick = (day: Date) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    const status = getDayStatus(day);
    
    console.log('=== DAY CLICK START ===');
    console.log('Day clicked:', dayStr);
    console.log('Status:', status);
    console.log('PACT ID:', pact.id);
    console.log('toggleCompletion function:', typeof toggleCompletion);
    console.log('toggleSkip function:', typeof toggleSkip);
    
    try {
      if (status === 'completed') {
        console.log('Removing completion for:', dayStr);
        toggleCompletion(pact.id, dayStr);
      } else if (status === 'skipped') {
        console.log('Removing skip for:', dayStr);
        toggleSkip(pact.id, dayStr);
      } else if (status === 'missed' || status === 'today') {
        console.log('Adding completion for:', dayStr);
        toggleCompletion(pact.id, dayStr);
      }
      console.log('=== DAY CLICK END ===');
    } catch (error) {
      console.error('Error in handleDayClick:', error);
    }
  };

  const handleDayDoubleClick = (day: Date) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    const status = getDayStatus(day);
    
    console.log('=== DAY DOUBLE CLICK START ===');
    console.log('Day double-clicked:', dayStr);
    console.log('Status:', status);
    console.log('Skips remaining:', skipsRemaining);
    
    try {
      if ((status === 'missed' || status === 'today') && skipsRemaining > 0) {
        console.log('Adding skip for:', dayStr);
        toggleSkip(pact.id, dayStr);
      } else {
        console.log('Cannot skip - no skips remaining or invalid status');
      }
      console.log('=== DAY DOUBLE CLICK END ===');
    } catch (error) {
      console.error('Error in handleDayDoubleClick:', error);
    }
  };

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const goToCurrentMonth = () => setCurrentMonth(new Date());

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="relative p-8 pb-4">
          <button
            onClick={onClose}
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all duration-200"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
          
          <div className="pr-12">
            <h1 className="text-3xl font-bold text-deep-blue mb-2 leading-tight">{pact.name}</h1>
            <p className="text-deep-blue/60 text-lg">
              {format(createdDate, 'MMM dd')} — {format(endDate, 'MMM dd, yyyy')}
            </p>
          </div>
        </div>

        {/* Main Stats - Hero Section */}
        <div className="px-8 pb-6">
          <div className="bg-gradient-to-br from-soft-blue to-white rounded-3xl p-6 mb-6 border border-soft-blue/30">
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-vibrant-orange to-orange-500 rounded-2xl mx-auto mb-3">
                  <Flame className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl font-bold text-deep-blue mb-1">{currentStreak}</div>
                <div className="text-sm text-deep-blue/70 font-medium">Current Streak</div>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-deep-blue to-blue-600 rounded-2xl mx-auto mb-3">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl font-bold text-deep-blue mb-1">{completionRate}%</div>
                <div className="text-sm text-deep-blue/70 font-medium">Completion Rate</div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <span className="text-lg font-semibold text-deep-blue">Overall Progress</span>
              <span className="text-sm text-deep-blue/70 bg-soft-blue px-3 py-1 rounded-full font-medium">{totalCompleted}/{totalDaysElapsed} days</span>
            </div>
            <div className="w-full bg-soft-blue rounded-full h-4 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-vibrant-orange to-orange-500 h-4 rounded-full transition-all duration-700 ease-out"
                style={{ width: `${completionRate}%` }}
              />
            </div>
          </div>

          {/* Secondary Stats Grid */}
          <div className="grid grid-cols-4 gap-3 mb-8">
            <div className="text-center p-4 bg-green-50 rounded-2xl">
              <CheckCircle2 className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-deep-blue">{totalCompleted}</div>
              <div className="text-xs text-deep-blue/70 font-medium">Completed</div>
            </div>

            <div className="text-center p-4 bg-red-50 rounded-2xl">
              <X className="w-6 h-6 text-red-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-deep-blue">{totalMissed}</div>
              <div className="text-xs text-deep-blue/70 font-medium">Missed</div>
            </div>

            <div className="text-center p-4 bg-yellow-50 rounded-2xl">
              <SkipForward className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-deep-blue">{totalSkipped}</div>
              <div className="text-xs text-deep-blue/70 font-medium">Skipped</div>
            </div>

            <div className="text-center p-4 bg-blue-50 rounded-2xl">
              <Clock className="w-6 h-6 text-deep-blue mx-auto mb-2" />
              <div className="text-2xl font-bold text-deep-blue">{skipsRemaining}</div>
              <div className="text-xs text-deep-blue/70 font-medium">Skips Left</div>
            </div>
          </div>

          {/* Full Month Calendar View */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-deep-blue">Daily Progress</h3>
              <div className="flex items-center gap-2">
                <button onClick={prevMonth} className="p-2 hover:bg-soft-blue rounded-lg">
                  <ChevronLeft className="w-5 h-5 text-deep-blue" />
                </button>
                <button 
                  onClick={goToCurrentMonth}
                  className="text-lg font-semibold text-deep-blue min-w-[120px] text-center hover:bg-soft-blue rounded-lg px-2 py-1 transition-colors cursor-pointer"
                >
                  {format(currentMonth, 'MMM yyyy')}
                </button>
                <button onClick={nextMonth} className="p-2 hover:bg-soft-blue rounded-lg">
                  <ChevronRight className="w-5 h-5 text-deep-blue" />
                </button>
              </div>
            </div>
            
            <div className="bg-soft-blue/30 rounded-3xl p-4">
              <div className="grid grid-cols-7 gap-2 text-xs mb-3">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                  <div key={index} className="h-8 flex items-center justify-center text-deep-blue/70 font-semibold">
                    {day}
                  </div>
                ))}
              </div>
              
              <div className="grid grid-cols-7 gap-2">
                {calendarDays.map((day, index) => {
                  const status = getDayStatus(day);
                  const isCurrentMonth = isSameMonth(day, currentMonth);
                  const startOfPACT = startOfDay(createdDate);
                  const endOfPACT = startOfDay(endDate);
                  const dayStart = startOfDay(day);
                  const isPACTPeriod = dayStart >= startOfPACT && dayStart <= endOfPACT;
                  const isClickable = isPACTPeriod && isCurrentMonth && (status === 'completed' || status === 'skipped' || status === 'missed' || status === 'today');
                  
                  console.log(`Day ${format(day, 'd')}: status=${status}, isClickable=${isClickable}, isPACTPeriod=${isPACTPeriod}, isCurrentMonth=${isCurrentMonth}`);
                  
                  return (
                    <button
                      key={index}
                      onClick={() => {
                        console.log('Button clicked for day:', format(day, 'yyyy-MM-dd'));
                        if (isClickable) {
                          handleDayClick(day);
                        } else {
                          console.log('Day not clickable');
                        }
                      }}
                      onDoubleClick={() => {
                        console.log('Button double-clicked for day:', format(day, 'yyyy-MM-dd'));
                        if (isClickable) {
                          handleDayDoubleClick(day);
                        } else {
                          console.log('Day not double-clickable');
                        }
                      }}
                      disabled={!isClickable}
                      className={`h-8 w-8 rounded-xl flex items-center justify-center text-xs font-bold transition-all duration-200 ${
                        !isCurrentMonth 
                          ? 'text-gray-300'
                          : status === 'completed' 
                          ? 'bg-gradient-to-br from-green-400 to-green-500 text-white shadow-md hover:shadow-lg' 
                          : status === 'skipped'
                          ? 'bg-gradient-to-br from-yellow-400 to-yellow-500 text-white shadow-md hover:shadow-lg'
                          : status === 'missed'
                          ? 'bg-gradient-to-br from-red-400 to-red-500 text-white hover:shadow-lg'
                          : status === 'today'
                          ? 'bg-gradient-to-br from-vibrant-orange to-orange-500 text-white ring-2 ring-vibrant-orange/30 ring-offset-2 hover:shadow-lg'
                          : status === 'upcoming'
                          ? 'bg-white text-deep-blue/40 border border-soft-blue/50 hover:bg-soft-blue/50'
                          : 'bg-gray-100 text-gray-300'
                      } ${isClickable ? 'cursor-pointer hover:scale-105' : 'cursor-default'}`}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="grid grid-cols-2 gap-3 text-sm mb-6">
            <div className="flex items-center gap-3 p-3 bg-soft-blue/30 rounded-xl">
              <div className="w-4 h-4 bg-gradient-to-br from-green-400 to-green-500 rounded-lg"></div>
              <span className="text-deep-blue font-medium">Completed</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-soft-blue/30 rounded-xl">
              <div className="w-4 h-4 bg-gradient-to-br from-red-400 to-red-500 rounded-lg"></div>
              <span className="text-deep-blue font-medium">Missed</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-soft-blue/30 rounded-xl">
              <div className="w-4 h-4 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-lg"></div>
              <span className="text-deep-blue font-medium">Skipped</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-soft-blue/30 rounded-xl">
              <div className="w-4 h-4 bg-gradient-to-br from-vibrant-orange to-orange-500 rounded-lg"></div>
              <span className="text-deep-blue font-medium">Today</span>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-soft-blue/30 rounded-xl p-4 text-sm text-deep-blue/70">
            <p className="mb-1">• Click to mark complete/incomplete</p>
            <p>• Double-click to skip a day ({skipsRemaining} skips remaining)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PACTDetailModal;
